import { useCallback, useEffect, useRef, useState } from 'react';
import { HIVE_SURGE_PULSE_DURATION_MS } from '../config/hiveSurgePulseEffect';
import { playSoundEffect } from '../hooks/useSoundEffects';

export function mapHiveSurgeSlotToViewer(fx, slot, viewerUserId) {
  if (!fx || !slot || slot.zone == null || slot.slotIndex == null || fx.targetPlayerId == null) {
    return null;
  }
  const isOwnerViewer = String(fx.targetPlayerId) === String(viewerUserId);
  const prefix = isOwnerViewer ? 'your' : 'opponent';
  return {
    zone: `${prefix}_${slot.zone}`,
    index: slot.slotIndex,
  };
}

const HIVE_GROW_CHEVRON_STRATEGEM_IDS = new Set([62, 182, 224, 227]);

/** Deploy units that queue hiveSurgePulseFx on buffed ally slots (same chevrons as Hive Surge). */
const HIVE_GROW_CHEVRON_DEPLOY_UNIT_IDS = new Set([280]);

function hasHiveSurgeSelfGrowPulseFx(unit) {
  if (!unit?.instanceId) return false;
  if (HIVE_GROW_CHEVRON_DEPLOY_UNIT_IDS.has(Number(unit.id))) return false;
  const fx = unit.hiveSurgePulseFx;
  return (
    fx != null
    && Array.isArray(fx.slots)
    && fx.slots.length > 0
    && typeof fx.pulseSeq === 'number'
    && fx.pulseSeq > 0
  );
}

function isHiveSurgePulseStrategem(s) {
  if (!s || s.instanceId == null) return false;
  if (!HIVE_GROW_CHEVRON_STRATEGEM_IDS.has(Number(s.id))) return false;
  const t = (s.type && String(s.type)) || '';
  if (!t) return true;
  return /strategem/i.test(t);
}

function isHiveSurgePulseDeployUnit(unit) {
  if (!unit || unit.instanceId == null) return false;
  return HIVE_GROW_CHEVRON_DEPLOY_UNIT_IDS.has(Number(unit.id));
}

function hiveSurgeSelfGrowDedupeKey(ownerId, unit) {
  const pulseSeq = unit.hiveSurgePulseFx?.pulseSeq ?? 0;
  return `hsselfgrow-${ownerId}-${unit.instanceId}-${pulseSeq}`;
}

function seedHiveSurgePulseProcessed(gameState, processedRef) {
  Object.keys(gameState.state).forEach((pid) => {
    const player = gameState.state[pid];
    player?.completedOrders?.forEach((st) => {
      if (isHiveSurgePulseStrategem(st)) {
        processedRef.current.add(`hs${st.id}-${pid}-${st.instanceId}`);
      }
    });
    ['front', 'flank'].forEach((zone) => {
      (player?.playedOrders?.[zone] || []).forEach((unit) => {
        if (isHiveSurgePulseDeployUnit(unit) && unit.hiveSurgePulseFx) {
          processedRef.current.add(`hsdeploy-${pid}-${unit.instanceId}`);
        }
        if (hasHiveSurgeSelfGrowPulseFx(unit)) {
          processedRef.current.add(hiveSurgeSelfGrowDedupeKey(pid, unit));
        }
      });
    });
  });
}

/**
 * Play Hive Surge grow chevrons on battlefield slots (shared by strategems and redeem grow buffs).
 */
export function activateHiveSurgePulseSlots(fx, viewerUserId, setActiveHiveSurgePulses) {
  if (!fx || !Array.isArray(fx.slots) || fx.slots.length === 0) {
    return;
  }
  if (viewerUserId == null) {
    throw new Error('activateHiveSurgePulseSlots: viewerUserId is required');
  }
  fx.slots.forEach((slot, idx) => {
    const viewerSlot = mapHiveSurgeSlotToViewer(fx, slot, viewerUserId);
    if (!viewerSlot) {
      return;
    }
    const animKey = `hsgrow-${idx}-${Date.now()}`;
    setActiveHiveSurgePulses((prev) => [
      ...prev,
      { key: animKey, viewerSlot, animationKey: animKey },
    ]);
    window.setTimeout(() => {
      setActiveHiveSurgePulses((prev) => prev.filter((p) => p.key !== animKey));
    }, HIVE_SURGE_PULSE_DURATION_MS + 120);
  });
}

/**
 * Strategems 62 (Hive Surge), 182 (Gift of the Rift), 224 (Divine Favor), 227 (Praise the Gods): HiveSurgePulseEffect grow chevrons
 * when `hiveSurgePulseFx.slots` is set (sounds differ per useStrategemSoundEffects).
 * Deploy unit 280 (Resistance Commander) and redeem unit 254 use the same chevrons via deploy/redeem metadata.
 */
export function useHiveSurgePulse(gameState, viewerUserId) {
  const [activeHiveSurgePulses, setActiveHiveSurgePulses] = useState([]);
  const processedRef = useRef(new Set());
  const seededRef = useRef(false);
  const gameIdRef = useRef(null);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) return;

    const gid = gameState.gameId ?? null;
    if (gid != null && gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      seededRef.current = false;
      processedRef.current = new Set();
      setActiveHiveSurgePulses([]);
    }

    if (!seededRef.current) {
      seedHiveSurgePulseProcessed(gameState, processedRef);
      seededRef.current = true;
      return;
    }

    Object.keys(gameState.state).forEach((casterId) => {
      const player = gameState.state[casterId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach((strategem) => {
        if (!isHiveSurgePulseStrategem(strategem)) return;
        const fx = strategem.hiveSurgePulseFx;
        if (!fx || !Array.isArray(fx.slots) || fx.slots.length === 0) return;

        const dedupeKey = `hs${strategem.id}-${casterId}-${strategem.instanceId}`;
        if (processedRef.current.has(dedupeKey)) return;
        processedRef.current.add(dedupeKey);

        activateHiveSurgePulseSlots(fx, viewerUserId, setActiveHiveSurgePulses);
      });
    });

    Object.keys(gameState.state).forEach((ownerId) => {
      const player = gameState.state[ownerId];
      if (!player?.playedOrders) return;

      ['front', 'flank'].forEach((zone) => {
        (player.playedOrders[zone] || []).forEach((deployUnit) => {
          if (!isHiveSurgePulseDeployUnit(deployUnit)) return;
          const fx = deployUnit.hiveSurgePulseFx;
          if (!fx || !Array.isArray(fx.slots) || fx.slots.length === 0) return;

          const dedupeKey = `hsdeploy-${ownerId}-${deployUnit.instanceId}`;
          if (processedRef.current.has(dedupeKey)) return;
          processedRef.current.add(dedupeKey);

          activateHiveSurgePulseSlots(fx, viewerUserId, setActiveHiveSurgePulses);
          if (Number(deployUnit.id) === 280) {
            playSoundEffect('/sounds/hive_surge.mp3', 0.6);
          }
        });
      });
    });

    Object.keys(gameState.state).forEach((ownerId) => {
      const player = gameState.state[ownerId];
      if (!player?.playedOrders) return;

      ['front', 'flank'].forEach((zone) => {
        (player.playedOrders[zone] || []).forEach((growUnit) => {
          if (!hasHiveSurgeSelfGrowPulseFx(growUnit)) return;
          const fx = growUnit.hiveSurgePulseFx;
          if (!fx || !Array.isArray(fx.slots) || fx.slots.length === 0) return;

          const dedupeKey = hiveSurgeSelfGrowDedupeKey(ownerId, growUnit);
          if (processedRef.current.has(dedupeKey)) return;
          processedRef.current.add(dedupeKey);

          activateHiveSurgePulseSlots(fx, viewerUserId, setActiveHiveSurgePulses);
          playSoundEffect('/sounds/hive_surge.mp3', 0.6);
        });
      });
    });

    if (processedRef.current.size > 120) {
      const keys = Array.from(processedRef.current);
      keys.slice(0, keys.length - 80).forEach((k) => processedRef.current.delete(k));
    }
  }, [gameState, gameState?.stateVersion, viewerUserId]);

  const triggerGrowPulseFromFx = useCallback((fx) => {
    activateHiveSurgePulseSlots(fx, viewerUserId, setActiveHiveSurgePulses);
  }, [viewerUserId]);

  return { activeHiveSurgePulses, triggerGrowPulseFromFx };
}

export default useHiveSurgePulse;
